import type { ReactElement } from 'react';
import { Box, Text } from 'components/ui';
import { PrismThemeProvider } from 'theme';
import { DashboardGrid, DashboardPromptBar, DashboardPromptStarter } from './components';
import { SUMMARY_DASHBOARD_TEST_IDS } from './SummaryDashboard.constants';
import type { SummaryDashboardProps } from './SummaryDashboard.props';
import {
  dashboardDataErrorSx,
  summaryDashboardPopulatedRootSx,
  summaryDashboardRootSx,
} from './SummaryDashboard.styles';
import { useDashboardData } from './useDashboardData';
import { useDashboardGeneration } from './useDashboardGeneration';

const DATA_ERROR_MESSAGE = 'Could not load dashboard data. The tiles below show no values.';

// Renderer for dashboard-kind summary definitions. Intentionally standalone: it never touches the
// spreadsheet path (useFirmSummaryTab / the definition `/data` endpoint, which is spreadsheet-only
// on the backend). An empty dashboard shows the AI prompt starter; a populated one shows the grid
// under a pinned, collapsed copy of the prompt that built it, which expands for editing.
//
// Values come from one dashboard-data request per tab, scoped by the measurement-date chip and the
// fund filter (both of which stay on screen while a dashboard tab is active). Only `totals` tiles
// resolve today; the rest still render their configuration.
const SummaryDashboard = ({ activeDefinition, summaryType = 'firm' }: SummaryDashboardProps): ReactElement => {
  const dashboardDefinition = activeDefinition.dashboard_definition ?? null;
  const isPopulated = Boolean(dashboardDefinition?.components?.length);

  const { errorMessage, generate, isGenerating } = useDashboardGeneration({
    // Only a populated dashboard is revision context: sending the empty default would hand the
    // model a componentless "current dashboard" to reason about on a first generation.
    currentDefinition: isPopulated ? dashboardDefinition : null,
    definitionId: activeDefinition.id,
    summaryType,
  });

  const dashboardData = useDashboardData({
    definitionId: activeDefinition.id,
    // An empty dashboard has no components to resolve, so it fires no request.
    isEnabled: isPopulated,
    summaryType,
  });

  return (
    <PrismThemeProvider>
      <Box
        data-testid={SUMMARY_DASHBOARD_TEST_IDS.root}
        sx={isPopulated ? summaryDashboardPopulatedRootSx : summaryDashboardRootSx}>
        {dashboardDefinition && isPopulated ? (
          <>
            <DashboardPromptBar
              errorMessage={errorMessage}
              isGenerating={isGenerating}
              onSubmit={generate}
              prompt={dashboardDefinition.prompt}
            />
            {dashboardData.isError && (
              // Stated once above the grid rather than repeated on every tile, and kept separate
              // from the prompt bar's errorMessage, which means "generation failed".
              <Text
                component="p"
                data-testid={SUMMARY_DASHBOARD_TEST_IDS.dataError}
                sx={dashboardDataErrorSx}>
                {DATA_ERROR_MESSAGE}
              </Text>
            )}
            <DashboardGrid data={dashboardData} definition={dashboardDefinition} />
          </>
        ) : (
          <DashboardPromptStarter errorMessage={errorMessage} isGenerating={isGenerating} onSubmit={generate} />
        )}
      </Box>
    </PrismThemeProvider>
  );
};

export default SummaryDashboard;
